import React, { Fragment, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { isEmpty } from 'lodash-es';
import {
  AgoraVideoPlayer,
  createClient,
  createMicrophoneAndCameraTracks,
  ClientConfig,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react';
import { FlexBox, Heading3, CenterBox, Icons } from '@docon/docon-ui-library';
import { environment } from 'environments/environment';
import { bgColor, colors } from 'utilities/colorConfig';
import { useDispatch, useSelector } from 'react-redux';
import { getTeleSession, updateAgoraState } from 'redux/reducer/agora';
import { Mike, Video } from './icons';

const Root = styled(FlexBox)`
  width: 100vw;
  height: 100vh;
  height: -webkit-fill-available;
  z-index: 999;
  flex-direction: column;
  position: absolute;
  overflow: hidden;
  cursor: all-scroll;
  text-align: center;
  background: #3a3963;

  .local {
    width: 35%;
    height: 40%;
    z-index: 1001;
  }

  @media screen and (min-width: 800px) {
    right: 1rem;
    width: 25vw;
    height: 50vh;
    bottom: 60px;
    border-radius: 5px;
  }
`;

const VideoPlayer = styled(AgoraVideoPlayer)`
  width: 100%;
  height: 100%;
  border-radius: 5px;
  z-index: 1000;
  right: 0;
  bottom: 0;
  position: absolute;
  overflow: hidden;
`;

const IconWrapper = styled(CenterBox)`
  width: 34px;
  height: 34px;
  color: ${({ $unmute }) => $unmute ? colors.error : colors.primaryColor};
  background-color: ${colors.white};
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
  }
`;

const Footer = styled(FlexBox)`
  width: 100%;
  bottom: 12px;
  position: absolute;
  justify-content: space-evenly;
  align-items: center;
  z-index: 1002;

  .end-call {
    width: 50px;
    height: 50px;
    color: ${colors.white};
    transform: rotate(135deg);
    background-color: ${bgColor.error};
  }
`;

const config: ClientConfig = {
  mode: 'rtc', codec: 'h264',
};

const appId: string = environment.REACT_APP_AGORA_APPID || ''; //ENTER APP ID HERE

// the create methods in the wrapper return a hook
// the create method should be called outside the parent component
// this hook can be used the get the client/stream in any component
const useClient = createClient(config);
const useMicrophoneAndCameraTracks = createMicrophoneAndCameraTracks();

const Controls = (props: {
  tracks: [IMicrophoneAudioTrack, ICameraVideoTrack];
  setStart: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const dispatch = useDispatch();
  const client = useClient();
  const { tracks, setStart } = props;
  const [trackState, setTrackState] = useState({ video: true, audio: true });

  useEffect(() => {
    return () => {
      leaveChannel();
    };
  }, []);

  async function handleClick(event: any) {
    event.stopPropagation();
    const id = event?.currentTarget?.id;
    switch (id) {
      // mute/unmute handing
      case 'audio':
        await tracks[0].setEnabled(!trackState.audio);
        setTrackState((ps) => ({ ...ps, audio: !ps.audio }));
        break;
      case 'video':
        await tracks[1].setEnabled(!trackState.video);
        setTrackState((ps) => ({ ...ps, video: !ps.video }));
        break;
      case 'leave': {
        leaveChannel();
      }
        break;
    }
  }

  async function leaveChannel() {
    await client.leave();
    client.removeAllListeners();
    // we close the tracks to perform cleanup
    tracks[0].close();
    tracks[1].close();
    setStart(false);
    dispatch(updateAgoraState({ teleConsultSessionId: '', teleChannelUuid: '' }));
  }

  return (
    <Footer>
      <IconWrapper $unmute={!trackState.audio} id='audio' onClick={handleClick}>
        <Mike mute={trackState.audio} />
      </IconWrapper>
      <IconWrapper className='end-call' id='leave' onClick={handleClick}>
        <Icons.PhoneIcon />
      </IconWrapper>
      <IconWrapper $unmute={!trackState.video} id='video' onClick={handleClick}>
        <Video mute={trackState.video} />
      </IconWrapper>
    </Footer>
  );
};

const VideoConsult = ({ channelName }: any) => {
  const [userByUid, setUserByUid] = useState<any>({});
  const [start, setStart] = useState<boolean>(false);

  // using the hook to get access to the client object
  const client = useClient();
  // ready is a state variable, which returns true when the local tracks are initialized, untill then tracks variable is null
  const { ready, tracks } = useMicrophoneAndCameraTracks();

  function updateUsers(user: IAgoraRTCRemoteUser) {
    setUserByUid((prevUsers) => {
      return { ...prevUsers, [user.uid]: user };
    });
  }

  useEffect(() => {
    // function to initialise the SDK
    const init = async (name: string) => {
      client.on('user-published', async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        console.log('subscribe success');
        if (mediaType === 'video') {
          updateUsers(user);
        }
        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });

      client.on('user-unpublished', (user, type) => {
        console.log('unpublished', user, type);
        if (type === 'audio') {
          user.audioTrack?.stop();
        }
        if (type === 'video') {
          updateUsers(user);
        }
      });

      client.on('user-left', (user) => {
        console.log('leaving', user);
        setUserByUid((prevUsers) => delete prevUsers[user.uid]);
      });

      await client.join(appId, name, null, null);
      if (tracks) await client.publish([tracks[0], tracks[1]]);
      setStart(true);
    };

    if (channelName && ready && tracks) {
      console.log('init ready');
      init(channelName);
    }

  }, [channelName, client, ready, tracks]);

  if (!tracks) return null;

  const Node = isMobileOnly ? Fragment : Draggable;
  return (
    <Node>
      <Root>
        {ready && (
          <Controls tracks={tracks} setStart={setStart} />
        )}
        {
          start ? (
            <>
              {/* AgoraVideoPlayer component takes in the video track to render the stream,
            you can pass in other props that get passed to the rendered div */}
              <VideoPlayer className='local' videoTrack={tracks[1]} />
              {
                isEmpty(userByUid) ? (
                  <Heading3 color={colors.white} m='auto' p='10px'>
                    Waiting for the doctor to join...
                  </Heading3>
                ) : (
                  Object.keys(userByUid).map((uid) => {
                    const user = userByUid[uid];
                    if (user.videoTrack) {
                      return (
                        <VideoPlayer
                          key={user.uid}
                          className='remote'
                          videoTrack={user.videoTrack}
                        />
                      );
                    } else return null;
                  })
                )
              }
            </>
          ) : null
        }
      </Root>
    </Node>
  );
};

const AgoraPlayer = () => {
  const {
    teleConsultSessionId, teleChannelUuid,
  } = useSelector(state => state.agora);

  const dispatch = useDispatch();

  useEffect(() => {
    if (teleConsultSessionId)
      dispatch(getTeleSession(teleConsultSessionId));
  }, [teleConsultSessionId]);

  if (!teleChannelUuid) return null;
  return (
    <VideoConsult key={teleChannelUuid} channelName={teleChannelUuid} />
  );
};

export default AgoraPlayer;